import type { ResultImportItem } from "@synthesis/shared-services/external/application/types";
import { FileIconComponent, Tag } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import { RESOLUTION_BADGE_TEXT } from "../application-status-ui";

type ImportResultViewProps = {
  fileName?: string;
  data: ResultImportItem;
  variant?: "standalone" | "detail";
  className?: string;
};

const formatNumber = (value: number | null | undefined) => (value != null ? `№${String(value)}` : "—");

const formatCount = (value: number | null | undefined) => (value != null ? String(value) : "—");

const getDecisionTagStatus = (decision?: string | null) => {
  if (decision === "APPROVED") {
    return "SUCCESS" as const;
  }

  if (decision === "REJECTED") {
    return "ERROR" as const;
  }

  return "NEUTRAL" as const;
};

const getDecisionLabel = (decision?: string | null) => {
  if (decision === "APPROVED" || decision === "REJECTED") {
    return RESOLUTION_BADGE_TEXT[decision];
  }

  if (decision === "REVISION_REQUIRED") {
    return "Требуется доработка";
  }

  return "—";
};

type ResultRowProps = {
  label: string;
  children: React.ReactNode;
};

const ResultRow = ({ label, children }: ResultRowProps) => (
  <div className="border-b-neutrals-90 flex items-start gap-4 border-b">
    <span className="paragraph-sm text-type-minor w-[7.5rem] shrink-0 py-1">{label}</span>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

export const ImportResultView = ({ fileName, data, variant = "standalone", className }: ImportResultViewProps) => (
  <div className={cn("flex flex-col gap-6", className)}>
    {variant === "standalone" && fileName ? (
      <div className="flex flex-col gap-4">
        <h2 className="heading-h4 text-type-common">Архив итогового результата успешно импортирован</h2>
        <div className="flex min-w-0 items-start gap-3 py-2.5">
          <FileIconComponent className="size-6 shrink-0" ext="zip" />
          <span className="paragraph-base text-type-common min-w-0 break-all">{fileName}</span>
        </div>
      </div>
    ) : null}

    <div className="flex flex-col">
      <ResultRow label="Номер">
        <span className="paragraph-sm text-type-common py-1">{formatNumber(data.number)}</span>
      </ResultRow>

      <ResultRow label="Решение">
        <div className="py-1.5 pl-1">
          <Tag size="sm" className="w-fit max-w-full" status={getDecisionTagStatus(data.decision)}>
            {getDecisionLabel(data.decision)}
          </Tag>
        </div>
      </ResultRow>

      <ResultRow label="Файлы">
        <span className="paragraph-sm text-type-common py-1">{formatCount(data.filesImported)}</span>
      </ResultRow>

      <ResultRow label="Комментарии">
        <span className="paragraph-sm text-type-common py-1">{formatCount(data.commentsImported)}</span>
      </ResultRow>
    </div>
  </div>
);
